"use client";

import { useMediaQuery, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import PageLayout from "@/components/templates/PageLayout";
import conversationService from "@/services/conversation.service";
import useAuth from "@/contexts/auth-context";
import { useNotification } from "@/contexts/notification-context";
import useFilters from "@/hooks/use-filters";
import useGetCustomerId from "@/hooks/use-get-customer-id";
import { ChatMessages } from "@/messages/chat/chat.messages";

import ContactList from "./components/ContactList";
import ContactDataPanel from "./components/ContactDataPanel";
import CurrentChat from "./components/CurrentChat";
import LoadingContainer from "./components/ChatLoading";
import WhatsAppAccountInfo from "./components/WhatsAppAccountInfo";
import { CONTACT_FILTERS_INITIAL_VALUE, CUSTOMER_ID_FILTER } from "./constants";
import { useChatAttendanceActions } from "@/hooks/chat/use-chat-attendance-actions";
import { resolveInboxPlacement } from "@/types/conversation-filters.types";

import {
  forgetOpenConversation,
  mapConversationDetailToChat,
  readOpenConversation,
  rememberOpenConversation,
} from "./helpers";

type ChatPageProps = {
  messages: ChatMessages;
};

function ChatPage({ messages }: ChatPageProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const { user, isLoading: isLoadingUser } = useAuth();
  const { filters, changeFilters } = useFilters({ initialValue: CONTACT_FILTERS_INITIAL_VALUE });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isContactPanelExpanded, setIsContactPanelExpanded] = useState(false);
  const customerId = useGetCustomerId();
  const {
    isLoadingPlatform,
    isLoadingChats,
    selectedChat,
    chatError,
    chats,
    setSelectedChat,
    activeChatPlatformId,
    conversationView,
    conversationFilter,
    setConversationView,
    setConversationFilter,
    revealChatInList,
  } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOpeningLinkedConversation, setIsOpeningLinkedConversation] = useState(() => {
    const id = Number(searchParams.get("conversationId"));
    return id > 0;
  });
  const requestedConversationId = useRef<number | null>(null);
  const openedConversationId = useRef<number | null>(null);
  const openingConversationId = useRef<number | null>(null);
  const linkedChatRef = useRef<NonNullable<typeof chats>[number] | null>(null);
  const location = useLocation();
  const reportRowRef = useRef<Record<string, unknown> | null>(
    (location.state as { reportRow?: Record<string, unknown> } | null)?.reportRow
      ?? readOpenConversation()?.reportRow
      ?? null
  );
  const chatsRef = useRef(chats);
  chatsRef.current = chats;
  const attendanceActions = useChatAttendanceActions();

  const onSearch = (search: string) => {
    const updatedFilters = filters.map((filter) =>
      filter.property === "firstName" ? { ...filter, value: search } : filter
    );
    changeFilters(updatedFilters);
  };

  const initializeChatAndFilter = useCallback(() => {
    if (user && filters.length < 2) {
      const newFilter = [...filters, CUSTOMER_ID_FILTER(Number(customerId))];
      changeFilters(newFilter);
    }
  }, [user, filters, changeFilters, customerId]);

  useEffect(initializeChatAndFilter, [initializeChatAndFilter]);

  const clearConversationParam = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete("conversationId");
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    const row = (location.state as { reportRow?: Record<string, unknown> } | null)?.reportRow;
    if (row) reportRowRef.current = row;
  }, [location.state]);

  useEffect(() => {
    const param = Number(searchParams.get("conversationId"));
    const stored = readOpenConversation();
    const conversationId = param > 0 ? param : stored?.conversationId ?? null;
    if (!conversationId) return;
    setIsOpeningLinkedConversation(true);
    requestedConversationId.current = conversationId;
    if (stored?.reportRow && stored.conversationId === conversationId) {
      reportRowRef.current = stored.reportRow;
    }
    rememberOpenConversation({
      conversationId,
      reportRow: reportRowRef.current,
    });
  }, [searchParams]);

  useEffect(() => {
    const conversationId = requestedConversationId.current;
    if (!conversationId) return;
    // A abertura só termina quando a conversa selecionada é a pedida.
    // Enquanto não for, a URL e o sessionStorage permanecem para reaplicar.
    if (selectedChat?.conversationId === conversationId) {
      setIsOpeningLinkedConversation(false);
      if (openedConversationId.current !== conversationId) {
        openedConversationId.current = conversationId;
        openingConversationId.current = null;
        forgetOpenConversation();
        if (searchParams.get("conversationId")) clearConversationParam();
      }
      requestedConversationId.current = null;
      return;
    }
    if (isLoadingPlatform || isLoadingChats || !user || isLoadingUser) return;

    const applyPlacement = (source: Parameters<typeof resolveInboxPlacement>[0]) => {
      const placement = resolveInboxPlacement(source);
      if (conversationView === placement.view && conversationFilter === placement.filter) {
        return false;
      }
      if (conversationView !== placement.view) {
        setConversationView(placement.view);
      }
      setConversationFilter(placement.filter);
      openingConversationId.current = null;
      return true;
    };

    const applyChat = (chat: NonNullable<typeof chats>[number]) => {
      linkedChatRef.current = chat;
      if (applyPlacement(chat)) return;
      const listed = chatsRef.current?.find((item) => item.conversationId === conversationId);
      if (listed) setSelectedChat(listed);
      else revealChatInList(chat);
      setIsInitialLoad(false);
    };

    const failOpenConversation = () => {
      openingConversationId.current = null;
      setIsOpeningLinkedConversation(false);
    };

    const fromList = chatsRef.current?.find((chat) => chat.conversationId === conversationId);
    if (fromList) {
      applyChat(fromList);
      return;
    }

    if (linkedChatRef.current?.conversationId === conversationId) {
      applyChat(linkedChatRef.current);
      return;
    }

    const row = reportRowRef.current && Number(reportRowRef.current.id) === conversationId
      ? reportRowRef.current
      : null;

    if (row && applyPlacement(row as Parameters<typeof resolveInboxPlacement>[0])) return;

    if (openingConversationId.current === conversationId) return;
    openingConversationId.current = conversationId;

    const chatFromRow = (detail?: Record<string, unknown> | null) => {
      const contactId = Number(detail?.contactId ?? row?.contactId);
      const platformId = Number(detail?.platformId ?? row?.platformId ?? activeChatPlatformId);
      if (!contactId || !platformId) return null;
      return mapConversationDetailToChat({
        ...(row ?? {}),
        ...(detail ?? {}),
        conversationId,
        contactId,
        platformId,
        serviceQueueId: detail?.serviceQueueId ?? row?.customerServiceQueueId,
        serviceQueueName: detail?.serviceQueueName ?? row?.queueName,
      } as Parameters<typeof mapConversationDetailToChat>[0]);
    };

    conversationService
      .getConversationMessages(conversationId)
      .then((raw) => {
        if (requestedConversationId.current !== conversationId) return;
        const payload = raw as { conversationId?: number; contactId?: number; data?: Record<string, unknown> };
        const detail = (payload?.conversationId || payload?.contactId ? payload : payload?.data) as Record<string, unknown> | null;
        const loaded = chatsRef.current?.find((chat) => chat.conversationId === conversationId);
        const mapped = loaded ?? chatFromRow(detail);
        if (mapped) applyChat(mapped);
        else failOpenConversation();
      })
      .catch(async () => {
        if (requestedConversationId.current !== conversationId) return;
        try {
          const conversation = await conversationService.getConversation(conversationId);
          const mapped = chatFromRow({
            contactId: conversation.contactId,
            platformId: conversation.platformId,
            userId: conversation.userId,
            userName: conversation.userName,
            workflowId: conversation.workflowId,
            workflowName: conversation.workflowName,
            endDate: conversation.endDate,
            lastReceivedDate: conversation.lastReceivedDate,
            lastInterationDate: conversation.lastInterationDate,
          });
          if (mapped) {
            applyChat(mapped);
            return;
          }
        } catch {
          // Fall through to the report row.
        }
        const mapped = chatFromRow();
        if (mapped) applyChat(mapped);
        else failOpenConversation();
      });
  }, [
    searchParams,
    isLoadingPlatform,
    isLoadingChats,
    user,
    isLoadingUser,
    activeChatPlatformId,
    selectedChat?.conversationId,
    setSelectedChat,
    conversationView,
    conversationFilter,
    setConversationView,
    setConversationFilter,
    revealChatInList,
    clearConversationParam,
  ]);

  const isLoadingChatList = isLoadingPlatform || !!isLoadingChats || !user || isLoadingUser;
  const isLoadingCurrentChat = !selectedChat || isOpeningLinkedConversation;
  const shouldLoadChat = !chatError;
  const shouldDisplayError = !!chatError;
  const isChatsEmpty = !chats || chats.length <= 0;

  const handleBack = () => {
    setSelectedChat(null);
  };

  const showContactList = !isMobile || (!selectedChat && !isOpeningLinkedConversation);
  const showCurrentChat = !isMobile || !!selectedChat || isOpeningLinkedConversation;

  const openingConversationOverlay = isOpeningLinkedConversation ? (
    <MDBox
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 20,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        bgcolor: "background.paper",
      }}
    >
      <LoadingContainer />
      <MDTypography variant="button" color="text">
        {messages.openingConversation}
      </MDTypography>
    </MDBox>
  ) : null;

  return (
    <PageLayout title={messages.chatTitle} messages={messages}>
      {isMobile ? (
        // Mobile: skip PageContainer padding, use full available height
        <MDBox sx={{ height: "calc(100dvh - 120px)", overflow: "hidden", position: "relative" }}>
          {shouldLoadChat && (
            <Grid
              container
              spacing={0}
              sx={{
                height: "100%",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <MDBox
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  overflow: "hidden",
                }}
              >
                {/* Contact List - slides out to the left */}
                <MDBox
                  sx={{
                    position: "absolute",
                    inset: 0,
                    transform: selectedChat ? "translateX(-100%)" : "translateX(0)",
                    transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <MDBox px={1.5} pt={1.5} sx={{ flexShrink: 0 }}>
                    <WhatsAppAccountInfo messages={messages} />
                  </MDBox>
                  <MDBox sx={{ flexGrow: 1, minHeight: 0 }}>
                    <ContactList
                      messages={messages}
                      onSearch={onSearch}
                      isLoading={isLoadingChatList}
                      search={String(filters[0].value)}
                      chats={chats}
                      setIsInitialLoad={setIsInitialLoad}
                      isChatsEmpty={!!isChatsEmpty}
                    />
                  </MDBox>
                </MDBox>

                {/* Current Chat - slides in from the right */}
                <MDBox
                  sx={{
                    position: "absolute",
                    inset: 0,
                    transform: selectedChat ? "translateX(0)" : "translateX(100%)",
                    transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  <CurrentChat
                    messages={messages}
                    isLoadingCurrentChat={isLoadingCurrentChat}
                    user={user}
                    chats={chats}
                    isInitialLoad={isInitialLoad}
                    setIsInitialLoad={setIsInitialLoad}
                    onBack={handleBack}
                    attendanceActions={attendanceActions}
                  />
                </MDBox>
              </MDBox>

            {openingConversationOverlay}

            {shouldDisplayError && (
              <Grid
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="70vh"
                size={12}
              >
                <AlertBlock
                  title={messages.errorToLoadChatTitle}
                  description={"error.message"}
                  alt={messages.errorToLoadChatAlt}
                />
              </Grid>
            )}
          </Grid>
        )}
      </MDBox>
    ) : (
      <PageContainer>
        {shouldLoadChat && (
          <MDBox
            sx={{
              height: "calc(var(--full-vh) - 200px)",
              minHeight: "70vh",
              position: "relative",
              overflow: "hidden",
              display: "flex",
              gap: 3,
            }}
          >
            <MDBox
              sx={{
                width: "25%",
                minWidth: 260,
                maxWidth: 360,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                flexShrink: 0,
              }}
            >
              <WhatsAppAccountInfo messages={messages} />
              <MDBox sx={{ flexGrow: 1, minHeight: 0 }}>
                <ContactList
                  messages={messages}
                  onSearch={onSearch}
                  isLoading={isLoadingChatList}
                  search={String(filters[0].value)}
                  chats={chats}
                  setIsInitialLoad={setIsInitialLoad}
                  isChatsEmpty={!!isChatsEmpty}
                />
              </MDBox>
            </MDBox>

            <MDBox sx={{ flex: 1, minWidth: 0, height: "100%" }}>
              <CurrentChat
                messages={messages}
                isLoadingCurrentChat={isLoadingCurrentChat}
                user={user}
                chats={chats}
                isInitialLoad={isInitialLoad}
                setIsInitialLoad={setIsInitialLoad}
                attendanceActions={attendanceActions}
              />
            </MDBox>

            <MDBox
              sx={{
                width: isContactPanelExpanded ? "25%" : 56,
                minWidth: isContactPanelExpanded ? 260 : 56,
                maxWidth: isContactPanelExpanded ? 360 : 56,
                height: "100%",
                flexShrink: 0,
                transition: "width 0.25s ease, min-width 0.25s ease, max-width 0.25s ease",
              }}
            >
              <ContactDataPanel
                messages={messages}
                isExpanded={isContactPanelExpanded}
                onToggle={() => setIsContactPanelExpanded((prev) => !prev)}
                attendanceActions={attendanceActions}
              />
            </MDBox>

            {openingConversationOverlay}

            {shouldDisplayError && (
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="70vh"
                width="100%"
                position="absolute"
              >
                <AlertBlock
                  title={messages.errorToLoadChatTitle}
                  description={"error.message"}
                  alt={messages.errorToLoadChatAlt}
                />
              </MDBox>
            )}
          </MDBox>
        )}
      </PageContainer>
    )}
    </PageLayout>
  );
}

export default ChatPage;
